import { Taskflow, TaskflowTask } from './types';

const notLockedDueDate = (overrides: Partial<TaskflowTask['due']>): TaskflowTask['due'] => ({
    manual: false,
    manualNote: '',
    mode: 'add',
    days: '0',
    basis: 'cal',
    relTo: 'matter',
    relMatterDate: '',
    relMemberTaskId: '',
    lock: false,
    lockDescription: '',
    ...overrides,
});

const epoTasks: TaskflowTask[] = [
    {
        id: '1', title: 'Second-eyes check of data entry', lane: 'records',
        description: 'Second-eyes check of first-eyes data entry into Inprotech.',
        template: 'None', startStatus: 'Not Started', taskType: 'Filing',
        assignMode: 'resp', assignValue: { value: 'Acting', label: 'Acting' }, priority: 'low', effort: '00:15',
        due: notLockedDueDate({ lockDescription: 'This is a same-day internal check — nothing to lock, it can\'t be pushed out anyway.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: true, excludeTaskIds: [] },
    },
    {
        id: '2', title: 'Report to client', lane: 'fee',
        description: 'Report to client using reporting template — include details of the communication, deadline booked, and link to the communication on iManage.',
        template: 'Client Reporting — EPO 94(3) response', startStatus: 'Not Started', taskType: 'Client Reporting',
        assignMode: 'resp', assignValue: { value: 'Acting', label: 'Acting' }, priority: 'med', effort: '00:30',
        due: notLockedDueDate({ days: '30', relTo: 'matter', relMatterDate: 'Communication Received', lock: true, lockDescription: 'The client\'s one-month reporting deadline can\'t be extended — a due-date recalculation elsewhere in the flow won\'t move it.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '3', title: 'Draft invoice, standard wording', lane: 'sec',
        description: 'Generate draft invoice using standard wording.',
        template: 'Standard invoice wording', startStatus: 'Not Started', taskType: 'Invoicing',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:15',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '2', lockDescription: 'Not a client-facing deadline — safe to recalculate if something upstream shifts.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '4', title: 'Approve invoice', lane: 'fee',
        description: 'Approve the draft invoice prepared by Sec Team.',
        template: 'None', startStatus: 'Waiting', taskType: 'Invoicing',
        assignMode: 'resp', assignValue: { value: 'Responsible', label: 'Responsible' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '3', lockDescription: 'Not a client-facing deadline — safe to recalculate if something upstream shifts.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '5', title: 'Finalise + send invoice, standard wording', lane: 'sec',
        description: 'Finalise invoice and send to client using standard wording (can be delegated to the fee-earner to send).',
        template: 'Standard invoice wording', startStatus: 'Waiting', taskType: 'Invoicing',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:10',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '4', lockDescription: 'Not a client-facing deadline — safe to recalculate if something upstream shifts.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '6', title: 'Instructions received?', lane: 'sec', isGate: true,
        description: 'Monitor for instructions received from the client on the office action response.',
        template: 'None', startStatus: 'Waiting', taskType: 'Monitoring',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'med', effort: '00:05',
        due: notLockedDueDate({ relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'This is the deadline itself, not a step counting down to it — nothing to lock.' }),
        outcome: { question: 'Draft response requested?', options: ['Yes', 'No'] },
        onCompletionRules: [
            { id: 'oc-6-1', fromTaskId: '6', toStatus: 'Cancelled' },
            { id: 'oc-6-2', fromTaskId: '7', toStatus: 'Cancelled' },
            { id: 'oc-6-3', fromTaskId: '8', toStatus: 'Cancelled' },
            { id: 'oc-6-4', fromTaskId: '9', toStatus: 'Not Started', ifOutcome: 'Yes' },
            { id: 'oc-6-5', fromTaskId: '10a', toStatus: 'Not Started', ifOutcome: 'Yes' },
            { id: 'oc-6-6', fromTaskId: '12', toStatus: 'Not Started', ifOutcome: 'No' },
        ],
        matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '7', title: 'Reminder to client', lane: 'sec',
        description: 'Send reminder to client using standard wording — only needed if task 6 is still open when this falls due.',
        template: 'Reminder — response deadline approaching', startStatus: 'Not Started', taskType: 'Reminder',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ mode: 'sub', days: '30', relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'Reminders track the response deadline — recalculating the deadline should move these too.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '8', title: 'Reminder to client', lane: 'sec',
        description: 'Send reminder to client using standard wording — only needed if task 6 is still open when this falls due.',
        template: 'Reminder — response deadline approaching', startStatus: 'Not Started', taskType: 'Reminder',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ mode: 'sub', days: '14', relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'Reminders track the response deadline — recalculating the deadline should move these too.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '9', title: 'Reminder to client', lane: 'fee',
        description: 'Send reminder to client — only needed if task 6 is still open when this falls due.',
        template: 'Reminder — response deadline approaching', startStatus: 'Waiting', taskType: 'Reminder',
        assignMode: 'resp', assignValue: { value: 'Acting', label: 'Acting' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ mode: 'sub', days: '7', relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'Reminders track the response deadline — recalculating the deadline should move these too.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '10a', title: 'Draft response, and email to client', lane: 'fee',
        description: 'Only reached when task 6 completes with the outcome set to Yes. Draft the substantive response and email it to the client — this one has no standard wording, it\'s bespoke to the office action.',
        template: 'None', startStatus: 'Waiting', taskType: 'Correspondence',
        assignMode: 'resp', assignValue: { value: 'Acting', label: 'Acting' }, priority: 'med', effort: '01:00',
        due: { manual: true, manualNote: 'Deadline for completion = manual, dependent on client requirements — there\'s no formula for this one. The fee-earner sets it by hand once they see what the client actually needs.', mode: 'add', days: '', basis: 'cal', relTo: 'matter', relMatterDate: '', relMemberTaskId: '', lock: false, lockDescription: '' },
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '10b', title: 'Monitor for instructions to file draft response', lane: 'sec',
        description: 'Only reached when task 6 completes with the outcome set to Yes. Watches for the client\'s go-ahead to file the drafted response.',
        template: 'None', startStatus: 'Waiting', taskType: 'Monitoring',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'med', effort: '00:05',
        due: notLockedDueDate({ relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'This is the deadline itself — nothing to lock.' }),
        onCompletionRules: [
            { id: 'oc-10b-1', fromTaskId: '12', toStatus: 'Not Started' },
            { id: 'oc-10b-2', fromTaskId: '11', toStatus: 'Cancelled' },
        ],
        matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '10c', title: 'Reminder re draft response', lane: 'sec',
        description: 'Send reminder to client re draft response — only needed if task 10b is still open when this falls due.',
        template: 'Reminder — response deadline approaching', startStatus: 'Not Started', taskType: 'Reminder',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ mode: 'sub', days: '7', relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'Tracks the response deadline, same as the other reminders.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '11', title: 'Draft / finalise response', lane: 'fee',
        description: 'Draft or finalise the response. Reached two ways: task 6 completing with no draft requested, or task 10b completing — this is a fan-in, handled by two separate on-completion rules pointing here.',
        template: 'None', startStatus: 'Waiting', taskType: 'Correspondence',
        assignMode: 'resp', assignValue: { value: 'Acting', label: 'Acting' }, priority: 'high', effort: '01:30',
        due: notLockedDueDate({ relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'This is the deadline itself — nothing to lock.' }),
        onCompletionRules: [{ id: 'oc-11-1', fromTaskId: '13', toStatus: 'Not Started' }],
        matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '12', title: 'Prepare OLF + draft report email', lane: 'sec',
        description: 'Prepare OLF and generate draft reporting email using template.',
        template: 'Client Reporting — EPO 94(3) response', startStatus: 'Waiting', taskType: 'Data Entry',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:20',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '12', lockDescription: 'Not a client-facing deadline — safe to recalculate if something upstream shifts.' }),
        onCompletionRules: [{ id: 'oc-12-1', fromTaskId: '14', toStatus: 'Not Started' }],
        matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '13', title: 'File response + finalise report', lane: 'fee',
        description: 'File response and finalise reporting email.',
        template: 'Client Reporting — EPO 94(3) response', startStatus: 'Waiting', taskType: 'Filing',
        assignMode: 'resp', assignValue: { value: 'Acting', label: 'Acting' }, priority: 'high', effort: '00:20',
        due: notLockedDueDate({ relTo: 'matter', relMatterDate: 'Response Deadline', lockDescription: 'This is the deadline itself — nothing to lock.' }),
        onCompletionRules: [
            { id: 'oc-13-1', fromTaskId: '15', toStatus: 'Not Started' },
            { id: 'oc-13-2', fromTaskId: '16', toStatus: 'Not Started' },
        ],
        matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '14', title: 'Check OLF2.0, clear deadline', lane: 'records',
        description: 'Check EPO OLF2.0 to determine response is filed, and clear the deadline.',
        template: 'None', startStatus: 'Waiting', taskType: 'Filing',
        assignMode: 'team', assignValue: { value: 'Records', label: 'Records' }, priority: 'low', effort: '00:10',
        due: notLockedDueDate({ relTo: 'member', relMemberTaskId: '14', lockDescription: 'Same-day check — nothing to lock.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '15', title: 'Draft invoice', lane: 'sec',
        description: 'Generate draft invoice.',
        template: 'Standard invoice wording', startStatus: 'Waiting', taskType: 'Invoicing',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:15',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '14', lockDescription: 'Not a client-facing deadline — safe to recalculate if something upstream shifts.' }),
        onCompletionRules: [{ id: 'oc-15-1', fromTaskId: '17', toStatus: 'Not Started' }],
        matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '16', title: 'Approve invoice', lane: 'fee',
        description: 'Approve invoice.',
        template: 'None', startStatus: 'Waiting', taskType: 'Invoicing',
        assignMode: 'resp', assignValue: { value: 'Responsible', label: 'Responsible' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '16', lockDescription: 'Not a client-facing deadline — safe to recalculate if something upstream shifts.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '17', title: 'Finalise invoice + send report email', lane: 'sec',
        description: 'Finalise invoice and send reporting email (can be delegated to the fee-earner to send reporting email).',
        template: 'Standard invoice wording', startStatus: 'Waiting', taskType: 'Invoicing',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:10',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '17', lockDescription: 'Not a client-facing deadline — safe to recalculate if something upstream shifts.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
];

const abandonmentTasks: TaskflowTask[] = [
    {
        id: '1', title: 'Client email received', lane: 'sec',
        description: 'Client sends instructions to abandon the case.',
        template: 'None', startStatus: 'Not Started', taskType: 'Correspondence',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ lockDescription: 'Entry point of the flow — nothing to lock.' }),
        onCompletionRules: [], matterStage: 'Abandonment requested', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '2', title: 'Prepare internal memo', lane: 'sec',
        description: 'Details added to abandonment sheet tab.',
        template: 'None', startStatus: 'Waiting', taskType: 'Correspondence',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:10',
        due: notLockedDueDate({ relTo: 'member', relMemberTaskId: '1', lockDescription: 'Same-day internal step — nothing to lock.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '3', title: 'Update internal memo', lane: 'sec',
        description: 'Boxes on abandonment sheet ticked and FE initials added.',
        template: 'None', startStatus: 'Waiting', taskType: 'Correspondence',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ relTo: 'member', relMemberTaskId: '2', lockDescription: 'Same-day internal step — nothing to lock.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '4', title: 'Send internal memo', lane: 'sec',
        description: 'Instructions to Formalities to abandon case prepared.',
        template: 'None', startStatus: 'Waiting', taskType: 'Correspondence',
        assignMode: 'team', assignValue: { value: 'Sec Team', label: 'Sec Team' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ relTo: 'member', relMemberTaskId: '3', lockDescription: 'Same-day internal step — nothing to lock.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '5', title: 'Internal department updated', lane: 'fee',
        description: 'Instructions to abandon case sent to Formalities.',
        template: 'None', startStatus: 'Waiting', taskType: 'Correspondence',
        assignMode: 'resp', assignValue: { value: 'Acting', label: 'Acting' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ relTo: 'member', relMemberTaskId: '4', lockDescription: 'Same-day internal step — nothing to lock.' }),
        onCompletionRules: [], matterStage: 'Instructed to Formalities', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '6', title: 'Internal department review', lane: 'records',
        description: 'Abandonment verified and details double-checked.',
        template: 'None', startStatus: 'Waiting', taskType: 'Filing',
        assignMode: 'team', assignValue: { value: 'Records', label: 'Records' }, priority: 'low', effort: '00:15',
        due: notLockedDueDate({ basis: 'work', days: '1', relTo: 'member', relMemberTaskId: '5', lockDescription: 'Not client-facing — safe to recalculate.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '7', title: 'Docket review', lane: 'records',
        description: 'Status of case checked on Inprotech.',
        template: 'None', startStatus: 'Waiting', taskType: 'Monitoring',
        assignMode: 'team', assignValue: { value: 'Records', label: 'Records' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ relTo: 'member', relMemberTaskId: '6', lockDescription: 'Same-day internal step — nothing to lock.' }),
        onCompletionRules: [], matterStage: '', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
    {
        id: '8', title: 'Database updated', lane: 'records',
        description: 'Inprotech updated with abandonment information.',
        template: 'None', startStatus: 'Waiting', taskType: 'Data Entry',
        assignMode: 'team', assignValue: { value: 'Records', label: 'Records' }, priority: 'low', effort: '00:05',
        due: notLockedDueDate({ relTo: 'member', relMemberTaskId: '7', lockDescription: 'Same-day internal step — nothing to lock.' }),
        onCompletionRules: [], matterStage: 'Case abandoned', access: { excludeTrigger: false, excludeTaskIds: [] },
    },
];

const buildTaskflow = (
    id: string,
    name: string,
    matterCategory: string[],
    matterType: string[],
    templateOptions: string[],
    matterDateOptions: string[],
    tasks: TaskflowTask[],
): Taskflow => ({
    id,
    name,
    status: 'draft',
    matterCategory,
    matterType,
    templateOptions,
    matterDateOptions,
    taskOrder: tasks.map(task => task.id),
    tasks: tasks.reduce((byId, task) => ({ ...byId, [task.id]: task }), {} as Record<string, TaskflowTask>),
});

export const createSeedTaskflows = (): Taskflow[] => [
    buildTaskflow(
        'epo-94-3-response',
        'EPO 94(3) office action response',
        ['Patents'],
        ['EPO'],
        ['Client Reporting — EPO 94(3) response', 'Standard invoice wording', 'Reminder — response deadline approaching', 'None'],
        ['Communication Received', 'Response Deadline', 'Filing Date'],
        epoTasks,
    ),
    buildTaskflow(
        'case-abandonment',
        'Case abandonment',
        ['Patents', 'Trademarks'],
        [],
        ['None'],
        ['Client Instruction Received'],
        abandonmentTasks,
    ),
];

export const createBlankTask = (id: string): TaskflowTask => ({
    id,
    title: 'Untitled task',
    lane: 'fee',
    description: '',
    template: 'None',
    startStatus: 'Not Started',
    taskType: 'Client Reporting',
    assignMode: 'resp',
    assignValue: { value: 'Acting', label: 'Acting' },
    allowDelegate: false,
    delegateMode: 'resp',
    delegateValue: undefined,
    priority: 'low',
    effort: '',
    due: notLockedDueDate({}),
    onCompletionRules: [],
    matterStage: '',
    access: { excludeTrigger: false, excludeTaskIds: [] },
});

export const createBlankTaskflow = (id: string): Taskflow => ({
    id,
    name: '',
    status: 'draft',
    matterCategory: [],
    matterType: [],
    templateOptions: ['None'],
    matterDateOptions: [],
    taskOrder: [],
    tasks: {},
});
